import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { ocrKeys } from '@/features/ocr';
import type { ExtractedDataItem, OcrResult } from '@/features/ocr';
import { ocrQaApi } from './ocr-qa.api';
import { ocrQaKeys } from './ocr-qa.queries';
import type {
  OcrQaTier,
  SubmitContainerVerdictParams,
  SubmitOcrQaPageVerdictParams,
  SubmitOcrQaVerdictParams,
  UpdateOcrQaFieldParams
} from '../types';

/**
 * Optimistically patch a single field inside the *container* results cache —
 * the data the shared `OcrResultsPage` renders via `useContainerResultsLive`.
 * Mirrors the OCR feature's own field-edit optimism so a QA reviewer's edit of
 * an already-verified field reflects instantly. Returns the previous value for
 * rollback.
 */
function patchContainerField(
  queryClient: QueryClient,
  containerId: number,
  resultId: number,
  key: string,
  value: string | null,
  pageNumber: number | null | undefined
): { data: OcrResult[] } | undefined {
  const cacheKey = ocrKeys.containerResults(containerId);
  const prev = queryClient.getQueryData<{ data: OcrResult[] }>(cacheKey);
  if (prev) {
    const patchField = (f: ExtractedDataItem) =>
      f.key === key && (pageNumber == null || f.page_number === pageNumber)
        ? { ...f, value, status: 'processed' as const }
        : f;
    queryClient.setQueryData<{ data: OcrResult[] }>(cacheKey, {
      ...prev,
      data: prev.data.map((r) =>
        r.id === resultId
          ? { ...r, extracted_data: r.extracted_data?.map(patchField) ?? null }
          : r
      )
    });
  }
  return prev;
}

export function useStartOcrQaMutation(
  tier: OcrQaTier,
  { silent = false }: { silent?: boolean } = {}
) {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrQa');

  return useMutation({
    mutationFn: (resultId: number) => ocrQaApi.start(tier, resultId),
    onSuccess: (_data, resultId) => {
      if (!silent) toast.success(t('startSuccess'));
      queryClient.invalidateQueries({ queryKey: ocrQaKeys.lists(tier) });
      queryClient.invalidateQueries({ queryKey: ocrQaKeys.metrics(tier) });
      queryClient.invalidateQueries({
        queryKey: ocrQaKeys.detail(tier, resultId)
      });
      queryClient.invalidateQueries({
        queryKey: [...ocrQaKeys.byTier(tier), 'container']
      });
    }
  });
}

/**
 * Every cache root a single-page QA verdict makes stale.
 *
 * A verdict moves `ocr_results.qa_stage`, and that field is read from two
 * independent roots: the tier's own QA caches (`ocrQaKeys`, backing the list,
 * metrics and the progress badge) and the shared OCR container-results cache
 * (`ocrKeys`) that `OcrResultsPage` renders — which is where the per-page
 * accept/reject buttons read `qa_stage` from. Invalidating only the QA root
 * left the buttons on screen after an accept until a hard reload.
 *
 * Exported so the contract is pinned by a test rather than by convention.
 */
export function ocrQaPageVerdictInvalidationKeys(
  tier: OcrQaTier,
  containerId: number
): QueryKey[] {
  return [ocrQaKeys.byTier(tier), ocrKeys.containerResults(containerId)];
}

export function useSubmitOcrQaVerdictMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrQa');

  return useMutation({
    mutationFn: (params: SubmitOcrQaVerdictParams) =>
      ocrQaApi.submitVerdict(params),
    // The verdict toast is raised here; without this the global MutationCache
    // raises a second one off the API message.
    meta: { skipGlobalSuccessToast: true },
    onSuccess: (_data, params) => {
      toast.success(t('verdictSuccess'));
      for (const queryKey of ocrQaPageVerdictInvalidationKeys(
        params.tier,
        params.containerId
      )) {
        queryClient.invalidateQueries({ queryKey });
      }
    }
  });
}

/**
 * Per-PAGE verdict. The reviewer's real unit — `useSubmitOcrQaVerdictMutation`
 * above acts on a whole OcrResult, which can cover anything from one page to
 * every page in the container.
 *
 * A reject bounces the page's result one tier back, and an accept may advance it
 * once its last page is accepted, so both cache roots have to go: the tier list
 * AND the shared OCR container-results cache the panel renders from.
 */
export function useSubmitPageVerdictMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrQa');

  return useMutation({
    mutationFn: (params: SubmitOcrQaPageVerdictParams) =>
      ocrQaApi.submitPageVerdict(params),
    meta: { skipGlobalSuccessToast: true },
    onSuccess: (_data, params) => {
      toast.success(t('verdictSuccess'));
      for (const queryKey of ocrQaPageVerdictInvalidationKeys(
        params.tier,
        params.containerId
      )) {
        queryClient.invalidateQueries({ queryKey });
      }
    }
  });
}

/**
 * Container-level verdict — accept or reject every actionable page of a
 * container at tier T as one unit. On success the container re-routes across
 * tiers (accept forwards it, reject bounces it one tier back), so we invalidate
 * `ocrQaKeys.all` — not just the current tier — plus the shared OCR container
 * cache so the detail view reflects the new stage + reviews.
 */
export function useSubmitContainerVerdictMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrQa');

  return useMutation({
    mutationFn: (params: SubmitContainerVerdictParams) =>
      ocrQaApi.submitContainerVerdict(params),
    meta: { skipGlobalSuccessToast: true },
    onSuccess: (_data, params) => {
      toast.success(t('verdictSuccess'));
      queryClient.invalidateQueries({ queryKey: ocrQaKeys.all });
      queryClient.invalidateQueries({
        queryKey: ocrKeys.containerResults(params.containerId)
      });
    }
  });
}

/**
 * QA-scoped field edit — routes through the tier's own field endpoint (gated by
 * `qa{tier}_ocr.edit`) so a QA reviewer who lacks `ocr.edit` can still correct a
 * value. Optimistically patches the rendered container cache, matching the OCR
 * feature's own edit flow.
 */
export function useUpdateOcrQaFieldMutation(tier: OcrQaTier) {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrQa');

  return useMutation({
    mutationFn: (params: Omit<UpdateOcrQaFieldParams, 'tier'>) =>
      ocrQaApi.updateField({ ...params, tier }),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('fieldUpdated')
    },
    onMutate: async ({ resultId, key, value, pageNumber, containerId }) => {
      if (containerId == null) return { prevContainer: undefined };
      await queryClient.cancelQueries({
        queryKey: ocrKeys.containerResults(containerId)
      });
      const prevContainer = patchContainerField(
        queryClient,
        containerId,
        resultId,
        key,
        value,
        pageNumber
      );
      return { prevContainer };
    },
    onError: (_err, { containerId }, context) => {
      if (context?.prevContainer && containerId != null) {
        queryClient.setQueryData(
          ocrKeys.containerResults(containerId),
          context.prevContainer
        );
      }
    },
    onSettled: (_data, _err, { containerId }) => {
      if (containerId != null) {
        queryClient.invalidateQueries({
          queryKey: ocrKeys.containerResults(containerId)
        });
      }
    }
  });
}
